import Vue from 'vue'
import router from '../../../router'
import moment from 'moment'

export const saleGoals = {
    state: {
        status: 'idle',
        saleGoals: null,
        pagination: {
            page: 1,
            totalPages: 0,
            perPage: 10,
            totalRecords: null
        },
        sortOptions: [
            {
                query: 'promos.start_at',
                label: 'Data rozpoczęcia'
            },
            {
                query: 'promos.end_at',
                label: 'Data zakończenia'
            },
            {
                query: 'promos.id',
                label: 'ID promocji'
            },
            {
                query: 'promos.name',
                label: 'Nazwa promocji'
            },
            {
                query: 'promos.state',
                label: 'Status'
            }
        ],
        sort: {
            by: 'promos.start_at',
            direction: 'desc'
        },
        filters: {
            startDate: {
                type: 'range-date',
                range: 'from',
                text: 'Od',
                title: 'Data',
                additionalText: 'Data od',
                groupName: 'range'
            },
            endDate: {
                type: 'range-date',
                range: 'to',
                text: 'Do',
                value: '',
                additionalText: 'Data do',
                groupName: 'range'
            },
            merchantIds: {
                type: 'ui-virtual-list',
                value: [],
                multi: false,
                searcher: false,
                label: 'Wybierz sklep',
                moduleName: 'merchants',
                fetchAction: 'merchantsFetch',
                getter: 'getMerchantsForSelect',
                disabledLinkGenerator: true
            },
            products: {
                type: 'ui-virtual-list',
                value: [],
                moduleName: 'products',
                getter: 'getProductsForFilters',
                label: 'Produkt'
            },
            promotionId: {
                type: 'ui-virtual-list',
                value: null,
                moduleName: 'promotions',
                label: 'Promocja',
                getter: 'getPromotionsForFilters'
            },
            promotionState: {
                type: 'ui-virtual-list',
                value: null,
                searcher: false,
                getter: 'getSaleGoalsStatuses',
                label: 'Status promocji',
                firstOption: 'Wszyscy'
            }
        }
    },
    mutations: {
        SET_SALEGOALS_STATUS(state, payload) {
            state.status = payload
        },
        SET_SALEGOALS(state, payload) {
            state.saleGoals = payload
        },
        SET_SALEGOALS_TOTALPAGES(state, payload) {
            state.pagination.totalPages = payload
        },
        SET_SALEGOALS_TOTALRECORDS(state, payload) {
            state.pagination.totalRecords = payload
        },
        SET_SALEGOALS_CURRENT(state, payload) {
            state.pagination.page = payload
        },
        SET_SALEGOALS_PERPAGE(state, payload) {
            state.pagination.perPage = payload
        },
        SET_SALEGOALS_FILTER(state, payload) {
            Vue.set(state.filters[payload.filter], 'value', payload.value)
        },
        RESET_SALEGOALS_FILTERS(state, payload) {
            Vue.set(state, 'filters', payload)
        },
        SET_SALEGOALS_SORT(state, payload) {
            state.sort = payload
        }
    },
    actions: {
        getGoalsToExcel({ getters }) {
            const params = getters.getSaleGoalsQueryParams
            return this.$api
                .get('reports/goalsToExcel', {
                    params,
                    responseType: 'arraybuffer'
                })
                .then(response => {
                    const today = Vue.moment().format('YYYY-MM-DD')

                    const fileLink = document.createElement('a')
                    const blob = new Blob([response.data], {
                        type: '"application/octet-stream"'
                    })
                    fileLink.href = window.URL.createObjectURL(blob)
                    fileLink.download = `Realizacja celów sprzedażowych-${today}.xlsx`
                    fileLink.click()
                })
        },
        async fetchSaleGoalsReport(_, payload) {
            try {
                const { data, status } = await this.$api.get('reports/achievement_of_sale_goals', { params: payload })

                if (status !== 200 || data?.data?.error) {
                    throw new Error(data?.data?.error || 'Error GET reports/achievement_of_sale_goals')
                }

                return data
            } catch (error) {
                console.error(error)
            }
        },
        async saleGoalsFetch({ commit, state, getters, dispatch }) {
            commit('SET_SALEGOALS_STATUS', 'loading')
            if (router.currentRoute.query.page) {
                commit('SET_SALEGOALS_CURRENT', +router.currentRoute.query.page)
            }
            if (router.currentRoute.query.show) {
                commit('SET_SALEGOALS_PERPAGE', +router.currentRoute.query.show)
            }
            const params = {
                page: state.pagination.page,
                per: state.pagination.perPage,
                direction: state.sort.direction,
                sort: state.sort.by,
                ...getters.getSaleGoalsQueryParams
            }
            const merchantId = getters.getSaleGoalsQueryParams['subscription|merchant_id']
            try {
                if (!merchantId || merchantId?.length === 0) {
                    commit('SET_SALEGOALS', null)
                    commit('SET_SALEGOALS_CURRENT', 1)
                    commit('SET_SALEGOALS_TOTALRECORDS', 0)
                    commit('SET_SALEGOALS_TOTALPAGES', 0)
                    return
                }

                const { data, pagination } = await dispatch('fetchSaleGoalsReport', params)

                if (!data) {
                    commit('SET_SALEGOALS', null)
                    commit('SET_SALEGOALS_CURRENT', 1)
                    commit('SET_SALEGOALS_TOTALRECORDS', 0)
                    commit('SET_SALEGOALS_TOTALPAGES', 0)
                    return
                }

                // Fetch promo thresholds
                const promoId = data.map(el => el.promo.id)?.join(',')
                const salesTargetThresholds = await dispatch('fetchSalesTargetThresholds', {
                    promoId,
                    per: promoId ? 99999 : null
                })
                const result = data.map(el => {
                    return {
                        ...el,
                        salesTargetThresholds: salesTargetThresholds.filter(thresholds => {
                            return thresholds.promo_id === el.promo.id
                        }).slice(0, 5)
                    }
                })
                commit('SET_SALEGOALS', result)
                commit('SET_SALEGOALS_TOTALRECORDS', +pagination[0].totalRecords)
                commit('SET_SALEGOALS_TOTALPAGES', +pagination[0].totalPages)
            } catch (err) {
                console.log(err)
            } finally {
                commit('SET_SALEGOALS_STATUS', 'idle')
            }
        },
        setSaleGoalsSort({ commit }, payload) {
            commit('SET_SALEGOALS_SORT', payload)
        },
        async saleGoalsFiltersFetch({ dispatch, getters }) {
            await dispatch('devicesFetch')
        },
        setSaleGoalsDefaultMerchant({ commit, getters }) {
            const merchants = getters.getMerchants
            const selectedMerchantId = getters.getSaleGoalsQueryParams['subscription|merchant_id']
            const noMerchantSelected = !selectedMerchantId || selectedMerchantId?.length === 0

            if (merchants[0] && noMerchantSelected) {
                commit('SET_SALEGOALS_FILTER', {
                    filter: 'merchantIds',
                    value: merchants[0].id
                })
            }
        }
    },
    getters: {
        getSaleGoalsQueryParams: state => {
            return {
                'promo|start_at': state.filters.startDate.value
                    ? `${moment
                        .utc(state.filters.startDate.value, 'DD-MM-YYYY')
                        .format('YYYY-MM-DD')},${moment
                        .utc(state.filters.startDate.value, 'DD-MM-YYYY')
                        .add(100, 'years')
                        .format('YYYY-MM-DD')}`
                    : null,
                'promo|end_at': state.filters.endDate.value
                    ? `${moment
                        .utc(state.filters.endDate.value, 'DD-MM-YYYY')
                        .subtract(100, 'years')
                        .format('YYYY-MM-DD')},${moment
                        .utc(state.filters.endDate.value, 'DD-MM-YYYY')
                        .add(1, 'days')
                        .format('YYYY-MM-DD')}`
                    : null,
                'subscription|merchant_id': state.filters.merchantIds.value,
                'promo_dict_product|dict_product_id': state.filters.products.value
                    ? state.filters.products.value
                    : null,
                'promo|state': state.filters.promotionState.value,
                'promo|id': state.filters.promotionId.value,
                includes:
                    'promo,promo.terms,merchant_usages,promo.company,merchant_usages.merchant'
            }
        },
        getSaleGoals: state => {
            return state.saleGoals
        },
        getSaleGoalsStatus: state => {
            return state.status
        },
        getSaleGoalsPagination: state => {
            return state.pagination
        },
        getSaleGoalsFilters: state => {
            return state.filters
        },
        getSaleGoalsSortOptions: state => {
            return state.sortOptions
        },
        getSaleGoalsSort: state => {
            return state.sort
        },
        getSaleGoalsStatuses: () => {
            const statuses = [
                {
                    value: 'active',
                    label: 'Aktywna'
                },
                {
                    value: 'finished',
                    label: 'Zakończona'
                }
            ]
            return statuses
        }
    }
}
